
import { useMemo, useState } from 'react';
import {
  Eye,
  ExternalLink,
  MessageSquare,
  Search,
  ThumbsDown,
  ThumbsUp,
  TrendingUp,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { painelInputClass } from '@/components/painel/cms/painel-form';
import { PainelGuideFeedbackMessageModal } from '@/components/painel/PainelGuideFeedbackMessageModal';
import {
  painelButtonOutlineClass,
  painelDescriptionMutedClass,
  painelInsetCardClass,
  painelTableShellClass,
} from '@/components/painel/painel-styles';
import { useGuideFeedback } from '@/components/help-center/guide-feedback-context';
import type { GuideNegativeFeedback } from '@/lib/guide-feedback';
import { getGuideCategoryPath } from '@/lib/guide-routes';
import { cn } from '@/lib/utils';

const wrap = painelTableShellClass;

const guideRowActionBtnClass =
  'rounded-xl text-blue-100 hover:bg-white/10 hover:text-white';

function formatUpdatedAt(iso: string) {
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function truncateMessage(message: string, max = 100) {
  const trimmed = message.trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max).trimEnd()}…`;
}

type StatCardProps = {
  label: string;
  value: string | number;
  hint: string;
  icon: typeof ThumbsUp;
  tone: 'emerald' | 'rose' | 'violet' | 'sky';
};

const STAT_TONE: Record<
  StatCardProps['tone'],
  { blob: string; ring: string; icon: string; value: string }
> = {
  emerald: {
    blob: 'from-emerald-500/20 to-transparent',
    ring: 'ring-emerald-400/25',
    icon: 'text-emerald-200',
    value: 'text-emerald-100',
  },
  rose: {
    blob: 'from-rose-500/20 to-transparent',
    ring: 'ring-rose-400/25',
    icon: 'text-rose-200',
    value: 'text-rose-100',
  },
  violet: {
    blob: 'from-violet-500/20 to-transparent',
    ring: 'ring-violet-400/25',
    icon: 'text-violet-200',
    value: 'text-violet-100',
  },
  sky: {
    blob: 'from-sky-500/20 to-transparent',
    ring: 'ring-sky-400/25',
    icon: 'text-sky-200',
    value: 'text-sky-100',
  },
};

function StatCard({ label, value, hint, icon: Icon, tone }: StatCardProps) {
  const colors = STAT_TONE[tone];
  return (
    <div className={cn(painelInsetCardClass, 'relative overflow-hidden p-5')}>
      <div
        className={cn(
          'pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br opacity-60 blur-2xl',
          colors.blob
        )}
        aria-hidden
      />
      <div className="relative flex items-start justify-between gap-3">
        <div
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-950/50 ring-1',
            colors.ring
          )}
        >
          <Icon className={cn('h-5 w-5', colors.icon)} aria-hidden />
        </div>
      </div>
      <p className={cn('relative mt-4 font-poppins text-2xl font-bold tabular-nums', colors.value)}>
        {value}
      </p>
      <p className="relative mt-1 font-poppins text-sm font-semibold text-white">{label}</p>
      <p className={cn('relative mt-0.5 font-inter text-xs', painelDescriptionMutedClass)}>{hint}</p>
    </div>
  );
}

export function PainelGuideFeedbackSection() {
  const { hydrated, summary, negativeFeedback } = useGuideFeedback();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState<GuideNegativeFeedback | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const sortedFeedback = useMemo(
    () =>
      [...negativeFeedback].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ),
    [negativeFeedback]
  );

  const filteredFeedback = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return sortedFeedback;
    return sortedFeedback.filter(
      (item) =>
        item.articleTitle.toLowerCase().includes(q) ||
        item.message.toLowerCase().includes(q) ||
        item.slug.toLowerCase().includes(q) ||
        (item.xatUsername?.toLowerCase().includes(q) ?? false)
    );
  }, [sortedFeedback, searchQuery]);

  const openMessage = (item: GuideNegativeFeedback) => {
    setSelectedItem(item);
    setModalOpen(true);
  };

  const satisfactionLabel =
    summary.satisfactionRate != null ? `${summary.satisfactionRate}%` : '—';

  return (
    <section className="space-y-5">
      <div>
        <h2 className="font-poppins text-lg font-bold text-white">Feedback dos visitantes</h2>
        <p className="mt-0.5 font-inter text-sm text-slate-400">
          Votos «Este tutorial foi útil?» e mensagens do formulário «Preciso de mais ajuda» nos
          artigos públicos.
        </p>
      </div>

      {!hydrated ? (
        <div className={cn(wrap, 'px-4 py-12 text-center text-sm text-slate-400')}>
          Carregando feedback…
        </div>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard
              label="Útil"
              value={summary.likes}
              hint="Visitantes que marcaram o tutorial como útil"
              icon={ThumbsUp}
              tone="emerald"
            />
            <StatCard
              label="Não ajudou"
              value={summary.dislikes}
              hint="Visitantes que indicaram que o tutorial não bastou"
              icon={ThumbsDown}
              tone="rose"
            />
            <StatCard
              label="Mensagens"
              value={summary.messages}
              hint={
                summary.guidesWithMessages > 0
                  ? `Em ${summary.guidesWithMessages} guia${summary.guidesWithMessages === 1 ? '' : 's'}`
                  : 'Nenhuma mensagem recebida ainda'
              }
              icon={MessageSquare}
              tone="violet"
            />
            <StatCard
              label="Satisfação"
              value={satisfactionLabel}
              hint={
                summary.satisfactionRate != null
                  ? 'Proporção de votos «útil» sobre o total de votos'
                  : 'Aguardando os primeiros votos'
              }
              icon={TrendingUp}
              tone="sky"
            />
          </div>

          <div className={wrap}>
            <div className="flex flex-col gap-4 border-b border-white/10 bg-white/[0.04] px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
              <div>
                <h3 className="font-poppins text-sm font-semibold text-blue-50">
                  Mensagens «Preciso de mais ajuda»
                </h3>
                <p className="mt-0.5 font-inter text-xs text-slate-400">
                  {filteredFeedback.length === sortedFeedback.length
                    ? `${sortedFeedback.length} mensagem${sortedFeedback.length === 1 ? '' : 'ens'} recebida${sortedFeedback.length === 1 ? '' : 's'}`
                    : `${filteredFeedback.length} de ${sortedFeedback.length} mensagens`}
                </p>
              </div>
              {sortedFeedback.length > 0 ? (
                <div className="relative w-full sm:max-w-xs">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                  <input
                    type="search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Buscar por guia, username ou mensagem…"
                    className={cn(painelInputClass, 'pl-10')}
                  />
                </div>
              ) : null}
            </div>

            {sortedFeedback.length === 0 ? (
              <div className="px-4 py-16 text-center">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.04]">
                  <MessageSquare className="h-7 w-7 text-slate-500" aria-hidden />
                </div>
                <p className="font-poppins text-sm font-semibold text-slate-300">
                  Nenhum feedback negativo ainda
                </p>
                <p className="mx-auto mt-1 max-w-sm font-inter text-xs text-slate-500">
                  Quando visitantes indicarem que precisam de mais ajuda, as mensagens aparecerão
                  aqui para revisão da equipe.
                </p>
              </div>
            ) : filteredFeedback.length === 0 ? (
              <div className="px-4 py-12 text-center font-inter text-sm text-slate-400">
                Nenhuma mensagem corresponde à busca.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[860px] border-collapse text-left font-inter text-sm">
                  <thead>
                    <tr className="border-b border-white/10 bg-white/[0.03]">
                      <th className="px-4 py-3 font-poppins text-xs font-semibold uppercase tracking-wide text-sky-200/70">
                        Guia
                      </th>
                      <th className="px-4 py-3 font-poppins text-xs font-semibold uppercase tracking-wide text-sky-200/70">
                        Username xat
                      </th>
                      <th className="px-4 py-3 font-poppins text-xs font-semibold uppercase tracking-wide text-sky-200/70">
                        Prévia
                      </th>
                      <th className="px-4 py-3 font-poppins text-xs font-semibold uppercase tracking-wide text-sky-200/70">
                        Recebido
                      </th>
                      <th className="px-4 py-3 text-right font-poppins text-xs font-semibold uppercase tracking-wide text-sky-200/70">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.06]">
                    {filteredFeedback.map((item) => (
                      <tr
                        key={item.id}
                        className="align-top transition-colors hover:bg-white/[0.03]"
                      >
                        <td className="max-w-[200px] px-4 py-3 md:max-w-[240px]">
                          <p className="line-clamp-2 font-poppins font-medium text-blue-50">
                            {item.articleTitle}
                          </p>
                          <p className="mt-1 truncate font-mono text-[11px] text-sky-300/70">
                            /{getGuideCategoryPath(item.categoryId)}/{item.slug}
                          </p>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          {item.xatUsername ? (
                            <span className="font-mono text-sm text-sky-200">@{item.xatUsername}</span>
                          ) : (
                            <span className="font-inter text-xs text-slate-500">—</span>
                          )}
                        </td>
                        <td className="max-w-md px-4 py-3 text-slate-400">
                          <p className="line-clamp-2 text-sm leading-relaxed">
                            {truncateMessage(item.message)}
                          </p>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-slate-400">
                          {formatUpdatedAt(item.createdAt)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className={cn(guideRowActionBtnClass, 'gap-1.5 px-3 font-inter text-xs')}
                              onClick={() => openMessage(item)}
                            >
                              <Eye className="h-3.5 w-3.5" />
                              Ver mensagem
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className={guideRowActionBtnClass}
                              asChild
                              aria-label="Abrir artigo no site"
                            >
                              <a
                                href={`/${getGuideCategoryPath(item.categoryId)}/${item.slug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <ExternalLink className="h-4 w-4" />
                              </a>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      <PainelGuideFeedbackMessageModal
        item={selectedItem}
        open={modalOpen}
        onOpenChange={(open) => {
          setModalOpen(open);
          if (!open) setSelectedItem(null);
        }}
      />
    </section>
  );
}
